import { FC, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import BookingStepLayout from "./BookingStepLayout";

// Property type icons (using SVG URLs from Figma)
const SeparatedPropertiesIcon =
  "https://www.figma.com/api/mcp/asset/fd1e9a33-ecae-45c3-b896-c9c6a0c0d85f";
const CollectedPropertyIcon =
  "https://www.figma.com/api/mcp/asset/792685ca-3c11-4734-9fb4-be188ff6b3f9";

interface IProps {
  onSelect: (type: "collected" | "separated") => void;
}

const BookingPropertyTypeSelection: FC<IProps> = ({ onSelect }) => {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState<
    "collected" | "separated" | null
  >(null);

  const handleCardClick = (type: "collected" | "separated") => {
    setSelectedType(type);
  };

  const handleNext = () => {
    if (selectedType) {
      onSelect(selectedType);
    }
  };

  return (
    <BookingStepLayout
      title={t("BOOKING.SELECT_PROPERTY_TYPE")}
      footer={
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!selectedType}
          sx={{
            backgroundColor: "#5B3FFF",
            borderRadius: "43px",
            padding: "12px 32px",
            fontSize: "16px",
            fontWeight: 700,
            fontFamily: "Tajawal",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#4A32CC",
            },
            "&:disabled": {
              backgroundColor: "#CCCCCC",
            },
          }}
        >
          {t("BOOKING.NEXT")}
        </Button>
      }
    >
      {/* Property Type Cards */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "center",
          alignItems: "stretch",
          flexWrap: "wrap",
          pt: 1,
        }}
      >
        {/* Separated Properties Card */}
        <Box
          onClick={() => handleCardClick("collected")}
          sx={{
            flex: "1 1 200px",
            maxWidth: "220px",
            minHeight: "240px",
            border:
              selectedType === "collected"
                ? "2px solid #5B3FFF"
                : "2px solid #E5E7EB",
            borderRadius: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px 16px",
            cursor: "pointer",
            backgroundColor:
              selectedType === "collected" ? "#F9F8FF" : "#FFFFFF",
            transition: "all 0.3s ease",
            boxShadow:
              selectedType === "collected"
                ? "0 4px 12px rgba(91, 63, 255, 0.15)"
                : "0 2px 8px rgba(0, 0, 0, 0.05)",
            "&:hover": {
              borderColor: "#5B3FFF",
              boxShadow: "0 4px 12px rgba(91, 63, 255, 0.15)",
              transform: "translateY(-2px)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              alignItems: "center",
            }}
          >
            {/* Icon */}
            <Box sx={{ width: "68px", height: "68px" }}>
              <div className="rounded-full px-5 py-4 bg-[#f2f1fc]">
                <g
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    style={{ display: "block", margin: "0 auto" }}
                  >
                    <path
                      d="M29.3334 29.3334H2.66669"
                      stroke="#5B3FFF"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                    <path
                      d="M22.6666 29.3333V7.99996C22.6666 5.4858 22.6666 4.22872 21.8856 3.44768C21.1045 2.66663 19.8474 2.66663 17.3333 2.66663H14.6666C12.1525 2.66663 10.8954 2.66663 10.1144 3.44768C9.33331 4.22872 9.33331 5.4858 9.33331 7.99996V29.3333"
                      stroke="#5B3FFF"
                      stroke-width="1.5"
                    />
                    <path
                      d="M28 29.3333V11.3333C28 9.46068 28 8.52437 27.5505 7.85177C27.356 7.5606 27.106 7.3106 26.8148 7.11604C26.1422 6.66663 25.206 6.66663 23.3333 6.66663"
                      stroke="#5B3FFF"
                      stroke-width="1.5"
                    />
                    <path
                      d="M4 29.3333V11.3333C4 9.46068 4 8.52437 4.44941 7.85177C4.64397 7.5606 4.89397 7.3106 5.18515 7.11604C5.85775 6.66663 6.79405 6.66663 8.66667 6.66663"
                      stroke="#5B3FFF"
                      stroke-width="1.5"
                    />
                    <path
                      d="M16 29.3334V25.3334"
                      stroke="#5B3FFF"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                    <path
                      d="M13.3333 16H18.6666"
                      stroke="#5B3FFF"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                    <path
                      d="M7.33331 14.6666H9.33331"
                      stroke="#5B3FFF"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                    <path
                      d="M7.33331 18.6666H9.33331"
                      stroke="#5B3FFF"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                    <path
                      d="M22.6667 14.6666H24.6667"
                      stroke="#5B3FFF"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                    <path
                      d="M22.6667 18.6666H24.6667"
                      stroke="#5B3FFF"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                    <path
                      d="M7.33331 10.6666H9.33331"
                      stroke="#5B3FFF"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                    <path
                      d="M22.6667 10.6666H24.6667"
                      stroke="#5B3FFF"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                    <path
                      d="M13.3333 20H18.6666"
                      stroke="#5B3FFF"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                    <path
                      d="M16 12C17.4727 12 18.6666 10.8061 18.6666 9.33329C18.6666 7.86053 17.4727 6.66663 16 6.66663C14.5272 6.66663 13.3333 7.86053 13.3333 9.33329C13.3333 10.8061 14.5272 12 16 12Z"
                      stroke="#5B3FFF"
                      stroke-width="1.5"
                    />
                  </svg>
                </g>
              </div>
            </Box>

            {/* Text */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Tajawal",
                  fontSize: "16px",
                  fontWeight: 700,
                  lineHeight: "22px",
                  color: "#1F2937",
                }}
              >
                {t("BOOKING.SEPARATED_PROPERTIES")}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Tajawal",
                  fontSize: "13px",
                  fontWeight: 400,
                  lineHeight: "18px",
                  color: "#6B7280",
                }}
              >
                {t("BOOKING.SEPARATED_PROPERTIES_DESC")}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Collected Property Card */}
        <Box
          onClick={() => handleCardClick("separated")}
          sx={{
            flex: "1 1 200px",
            maxWidth: "220px",
            minHeight: "240px",
            border:
              selectedType === "separated"
                ? "2px solid #5B3FFF"
                : "2px solid #E5E7EB",
            borderRadius: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px 16px",
            cursor: "pointer",
            backgroundColor:
              selectedType === "separated" ? "#F9F8FF" : "#FFFFFF",
            transition: "all 0.3s ease",
            boxShadow:
              selectedType === "separated"
                ? "0 4px 12px rgba(91, 63, 255, 0.15)"
                : "0 2px 8px rgba(0, 0, 0, 0.05)",
            "&:hover": {
              borderColor: "#5B3FFF",
              boxShadow: "0 4px 12px rgba(91, 63, 255, 0.15)",
              transform: "translateY(-2px)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              alignItems: "center",
            }}
          >
            {/* Icon */}
            <Box sx={{ width: "68px", height: "68px" }}>
              <div className="rounded-full px-5 py-4 bg-[#f2f1fc]">
                <g
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                  >
                    <path
                      d="M4 12L16 2.66663L28 12V26.6666C28 27.3739 27.719 28.0521 27.219 28.5522C26.7189 29.0523 26.0406 29.3333 25.3333 29.3333H6.66667C5.95942 29.3333 5.28115 29.0523 4.78105 28.5522C4.28095 28.0521 4 27.3739 4 26.6666V12Z"
                      stroke="#5B3FFF"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M12 29.3333V16H20V29.3333"
                      stroke="#5B3FFF"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </g>
              </div>
            </Box>

            {/* Text */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Tajawal",
                  fontSize: "16px",
                  fontWeight: 700,
                  lineHeight: "22px",
                  color: "#1F2937",
                }}
              >
                {t("BOOKING.COLLECTED_PROPERTY")}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Tajawal",
                  fontSize: "13px",
                  fontWeight: 400,
                  lineHeight: "18px",
                  color: "#6B7280",
                }}
              >
                {t("BOOKING.COLLECTED_PROPERTY_DESC")}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </BookingStepLayout>
  );
};

export default BookingPropertyTypeSelection;
